import type { ApolloQueryResult } from 'apollo-client'
import type { FetchResult } from 'apollo-link'
import type { DocumentNode } from 'graphql'

import GenerateRepositoryConfigGQL from '~/apollo/mutations/onboarding/generateRepositoryConfig.gql'
import ConfigGenerationRunGQL from '~/apollo/queries/onboarding/configGenerationRun.gql'
import FetchLatestConfigGenerationRunGQL from '~/apollo/queries/onboarding/latestConfigGenerationRun.gql'

import { RepoConfigInterface } from '~/store/repository/detail'
import {
  ConfigGenerationRunQuery,
  ConfigGenerationRunStatus,
  FetchLatestConfigGenerationRunQuery,
  GenerateRepositoryConfigMutation,
  VcsProviderChoices
} from '~/types/types'

type ApplyMutation = (
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  refetchQueries?: unknown
) => Promise<FetchResult<unknown>>

type FetchData = (
  query: DocumentNode,
  variables: Record<string, unknown>,
  refetch?: boolean
) => Promise<ApolloQueryResult<unknown>>

export interface GenerateConfigParams {
  repositoryId: string
  repo: string
  owner: string
  provider: VcsProviderChoices
}

export interface ConfigGenerationResult {
  status: ConfigGenerationRunStatus | null
  generatedConfig: RepoConfigInterface | null
}

export interface GenerateAndPollOpts {
  intervalMs?: number
  isCancelled?: () => boolean
  /**
   * When false, skip the `latestConfigGenerationRun` query and return
   * `generatedConfig: null` on PASS. Use when the caller only needs status
   * (e.g. onboarding navigates to another page that fetches the config).
   * Defaults to true.
   */
  fetchGenerated?: boolean
}

const DEFAULT_POLL_INTERVAL = 4000

/**
 * Returns true when the repo config has no analyzers, test patterns, or exclude patterns.
 * Used as precondition for auto-generating config on activation.
 */
export function isConfigEmpty(config: RepoConfigInterface): boolean {
  return (
    config.analyzers.length === 0 &&
    (config.test_patterns?.length ?? 0) === 0 &&
    (config.exclude_patterns?.length ?? 0) === 0
  )
}

const sleep = (ms: number, isCancelled?: () => boolean): Promise<void> =>
  new Promise((resolve) => {
    let interval: ReturnType<typeof setInterval> | undefined
    const done = () => {
      if (interval) clearInterval(interval)
      resolve()
    }
    const id = setTimeout(done, ms)
    if (isCancelled) {
      interval = setInterval(() => {
        if (isCancelled()) {
          clearTimeout(id)
          done()
        }
      }, 200)
    }
  })

async function pollRun(
  fetchData: FetchData,
  runId: string,
  opts: GenerateAndPollOpts
): Promise<ConfigGenerationRunStatus | null> {
  const intervalMs = opts.intervalMs ?? DEFAULT_POLL_INTERVAL

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (opts.isCancelled?.()) return null

    const response = (await fetchData(
      ConfigGenerationRunGQL,
      { runId },
      true
    )) as ApolloQueryResult<ConfigGenerationRunQuery>

    const status = response.data?.configGenerationRun?.status as
      | ConfigGenerationRunStatus
      | undefined

    if (!status) {
      throw new Error('Failed to fetch config generation run status')
    }

    if (
      status === ConfigGenerationRunStatus.Pass ||
      status === ConfigGenerationRunStatus.Fail ||
      status === ConfigGenerationRunStatus.Timo
    ) {
      return status
    }

    await sleep(intervalMs, opts.isCancelled)
  }
}

/**
 * Trigger backend config generation, poll until terminal status, fetch the generated config.
 *
 * Callers pass plugin helpers as params:
 *   generateAndPollConfig(this.$applyGraphqlMutation, this.$fetchGraphqlData, params)
 *
 * @returns status + generatedConfig (null when status is FAIL/TIMO or cancelled)
 */
export async function generateAndPollConfig(
  applyMutation: ApplyMutation,
  fetchData: FetchData,
  params: GenerateConfigParams,
  opts: GenerateAndPollOpts = {}
): Promise<ConfigGenerationResult> {
  const generateResponse = (await applyMutation(GenerateRepositoryConfigGQL, {
    repositoryId: params.repositoryId
  })) as FetchResult<GenerateRepositoryConfigMutation>

  const runId = generateResponse.data?.generateRepositoryConfig?.runId
  if (!runId) {
    throw new Error('Failed to start config generation')
  }

  const status = await pollRun(fetchData, runId, opts)

  if (status !== ConfigGenerationRunStatus.Pass) {
    return { status, generatedConfig: null }
  }

  if (opts.fetchGenerated === false) {
    return { status, generatedConfig: null }
  }

  const latestResponse = (await fetchData(
    FetchLatestConfigGenerationRunGQL,
    {
      repo: params.repo,
      owner: params.owner,
      provider: params.provider
    },
    true
  )) as ApolloQueryResult<FetchLatestConfigGenerationRunQuery>

  const runEdge = latestResponse.data?.repository?.configGenerationRuns?.edges?.[0]
  const generatedConfig = (runEdge?.node?.generatedConfig as RepoConfigInterface | null) ?? null

  return { status, generatedConfig }
}
